<script setup lang="ts">
import { Database, RefreshCw } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ExtractionSnapshot } from '@/lib/editor/extraction-table-extension';
import { snapshot as extractionSnapshot } from '@/routes/projects/extraction';

// ── Types ─────────────────────────────────────────────────────────────

interface Column {
    id: string;
    name: string;
    type: string;
}

interface ExtractionGrid {
    id: string;
    rows_count: number;
    columns: Column[];
}

// ── Props & emits ─────────────────────────────────────────────────────

const props = defineProps<{
    open: boolean;
    projectId: string;
    extractionGrid: ExtractionGrid | null;
}>();

const emit = defineEmits<{
    insert: [
        options: {
            gridId: string;
            columnIds: string[];
            filter: 'all' | 'validated';
            caption: string;
            snapshotData: ExtractionSnapshot;
        },
    ];
    close: [];
}>();

// ── State ─────────────────────────────────────────────────────────────

const caption = ref('');
const filter = ref<'all' | 'validated'>('validated');

// Selected column IDs (default = all)
const selectedColumnIds = ref<string[]>([]);

function initSelection() {
    selectedColumnIds.value =
        props.extractionGrid?.columns.map((c) => c.id) ?? [];
}

function resetDialogState() {
    initSelection();
    caption.value = '';
    filter.value = 'validated';
    error.value = null;
}

watch(
    [() => props.open, () => props.extractionGrid?.id],
    ([isOpen]) => {
        if (isOpen) {
            resetDialogState();
        }
    },
    { immediate: true },
);

function toggleColumn(id: string) {
    const idx = selectedColumnIds.value.indexOf(id);

    if (idx === -1) {
        selectedColumnIds.value = [...selectedColumnIds.value, id];
    } else {
        selectedColumnIds.value = selectedColumnIds.value.filter(
            (v) => v !== id,
        );
    }
}

// ── Estimated row count ───────────────────────────────────────────────

const estimatedRows = computed(() => props.extractionGrid?.rows_count ?? 0);

// ── Insert ────────────────────────────────────────────────────────────

const isInserting = ref(false);
const error = ref<string | null>(null);

async function handleInsert() {
    if (!props.extractionGrid || selectedColumnIds.value.length === 0) {
        return;
    }

    isInserting.value = true;
    error.value = null;

    try {
        const params = new URLSearchParams({ status: filter.value });

        for (const id of selectedColumnIds.value) {
            params.append('columns[]', id);
        }

        const res = await fetch(
            `${extractionSnapshot.url(props.projectId)}?${params.toString()}`,
            {
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            },
        );

        if (!res.ok) {
            throw new Error(
                `Erreur ${res.status} lors de la récupération du snapshot.`,
            );
        }

        const snapshotData: ExtractionSnapshot = await res.json();

        emit('insert', {
            gridId: props.extractionGrid.id,
            columnIds: selectedColumnIds.value,
            filter: filter.value,
            caption: caption.value,
            snapshotData,
        });
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Erreur inconnue.';
    } finally {
        isInserting.value = false;
    }
}

function onOpenChange(val: boolean) {
    if (!val) {
        emit('close');
    }
}
</script>

<template>
    <Dialog :open="props.open" @update:open="onOpenChange">
        <DialogContent class="max-w-lg">
            <DialogHeader>
                <DialogTitle class="flex items-center gap-2">
                    <Database class="h-4 w-4" />
                    Insérer un tableau d'extraction
                </DialogTitle>
                <DialogDescription>
                    Choisissez les colonnes et le filtre à figer dans le
                    document.
                </DialogDescription>
            </DialogHeader>

            <!-- No grid -->
            <div
                v-if="!props.extractionGrid"
                class="rounded border border-dashed p-8 text-center text-sm text-muted-foreground"
            >
                Aucune grille d'extraction trouvée pour ce projet.
            </div>

            <template v-else>
                <!-- Grid summary -->
                <p class="text-sm text-muted-foreground">
                    Grille :
                    <span class="font-medium text-foreground"
                        >{{ props.extractionGrid.rows_count }} article(s)</span
                    >
                </p>

                <!-- Caption -->
                <div class="space-y-1">
                    <Label for="et-caption">Titre du tableau</Label>
                    <Input
                        id="et-caption"
                        v-model="caption"
                        placeholder="Ex : Caractéristiques des études incluses"
                    />
                </div>

                <!-- Columns -->
                <div class="space-y-2">
                    <Label>Colonnes à inclure</Label>
                    <div class="grid grid-cols-2 gap-1.5">
                        <label
                            v-for="col in props.extractionGrid.columns"
                            :key="col.id"
                            class="flex cursor-pointer items-center gap-2 rounded border px-2 py-1.5 text-sm hover:bg-muted/40"
                            :class="{
                                'border-primary bg-primary/5':
                                    selectedColumnIds.includes(col.id),
                            }"
                        >
                            <input
                                type="checkbox"
                                class="accent-primary"
                                :checked="selectedColumnIds.includes(col.id)"
                                @change="toggleColumn(col.id)"
                            />
                            <span class="truncate">{{ col.name }}</span>
                        </label>
                    </div>
                    <p
                        v-if="selectedColumnIds.length === 0"
                        class="text-xs text-destructive"
                    >
                        Sélectionnez au moins une colonne.
                    </p>
                </div>

                <!-- Status filter -->
                <div class="space-y-2">
                    <Label>Lignes à inclure</Label>
                    <div class="flex gap-4 text-sm">
                        <label class="flex cursor-pointer items-center gap-2">
                            <input
                                type="radio"
                                v-model="filter"
                                value="validated"
                                class="accent-primary"
                            />
                            Validées uniquement
                        </label>
                        <label class="flex cursor-pointer items-center gap-2">
                            <input
                                type="radio"
                                v-model="filter"
                                value="all"
                                class="accent-primary"
                            />
                            Toutes
                        </label>
                    </div>
                </div>

                <!-- Row count hint -->
                <p class="text-xs text-muted-foreground">
                    ~{{ estimatedRows }} ligne(s) dans le snapshot
                    <template v-if="estimatedRows > 100">
                        <span
                            class="ml-1 font-medium text-amber-600 dark:text-amber-400"
                        >
                            (grand tableau — le rendu peut être lent)
                        </span>
                    </template>
                </p>

                <!-- Error -->
                <p
                    v-if="error"
                    class="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                    {{ error }}
                </p>
            </template>

            <DialogFooter>
                <Button variant="ghost" @click="emit('close')">Annuler</Button>
                <Button
                    data-tour="extraction-dialog-confirm"
                    :disabled="
                        !props.extractionGrid ||
                        selectedColumnIds.length === 0 ||
                        isInserting
                    "
                    @click="handleInsert"
                >
                    <RefreshCw
                        v-if="isInserting"
                        class="mr-2 h-4 w-4 animate-spin"
                    />
                    Insérer le snapshot
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
